import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY_CUSTOMER = { name: "", email: "", phone: "", address: { line1: "", city: "", state: "", postcode: "" } };

export default function Cart() {
  const { items, setQty, clear, subtotal } = useCart();
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [settings, setSettings] = useState(null);
  const [method, setMethod] = useState("square");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);

  const allDigital = items.length > 0 && items.every((i) => i.digital);
  const shipping = items.length === 0 || allDigital || subtotal >= 300 ? 0 : 10;
  const total = subtotal + shipping;

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const SQUARE_LINK = "https://square.link/u/FlI5wfG1";
  const METHODS = [
    { id: "square", title: "Card or Afterpay", note: "Secure payment via Square — card transaction fees apply" },
    { id: "bank", title: "Bank Transfer", note: "PayID / Osko — no fees" },
  ];

  const set = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));
  const setAddr = (k, v) => setCustomer((c) => ({ ...c, address: { ...c.address, [k]: v } }));

  const placeManualOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/orders/manual", {
        customer,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty, variant: i.variant })),
        shipping,
        payment_method: method,
      });
      setPlaced(data);
      clear();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPlacing(false);
    }
  };

  const customerValid =
    customer.name.trim() && /^\S+@\S+\.\S+$/.test(customer.email) && customer.address.line1.trim() &&
    customer.address.city.trim() && customer.address.postcode.trim();

  if (placed) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6" data-testid="order-placed-page">
        <div className="max-w-md w-full text-center">
          <div className="h-px w-16 bg-[#C5A059] mx-auto mb-8" />
          <h1 className="font-serif text-4xl font-light mb-3">Order reserved.</h1>
          <p className="text-sm text-[#4A4A4A] mb-8">
            Order <span className="text-[#9E7B3A]">{placed.order_number}</span> · Total <strong>${placed.total.toFixed(2)} AUD</strong>
          </p>
          {placed.payment_method === "square" ? (
            <div data-testid="square-payment-block">
              <a href={SQUARE_LINK} target="_blank" rel="noopener noreferrer" data-testid="square-pay-button"
                className="block w-full bg-[#1A1A1A] text-white text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#9E7B3A] transition-colors duration-300 mb-4">
                Pay ${placed.total.toFixed(2)} via Square
              </a>
              <p className="text-[11px] text-[#4A4A4A]/70 mb-8">
                Card and Afterpay accepted — Square's transaction fees apply. Enter the order total above when paying and use your order number as the note.
              </p>
            </div>
          ) : settings?.bank_bsb ? (
            <div className="border border-[#C5A059]/40 divide-y divide-[#C5A059]/15 text-sm mb-8 text-left" data-testid="bank-details-block">
              <div className="flex justify-between px-4 py-3"><span className="text-[#4A4A4A]">Bank</span><span>{settings.bank_name}</span></div>
              <div className="flex justify-between px-4 py-3"><span className="text-[#4A4A4A]">Account Name</span><span>{settings.bank_account_name}</span></div>
              <div className="flex justify-between px-4 py-3"><span className="text-[#4A4A4A]">BSB</span><span>{settings.bank_bsb}</span></div>
              <div className="flex justify-between px-4 py-3"><span className="text-[#4A4A4A]">Account Number</span><span>{settings.bank_account_number}</span></div>
              <div className="flex justify-between px-4 py-3"><span className="text-[#4A4A4A]">Reference</span><span className="text-[#9E7B3A]">{placed.order_number}</span></div>
            </div>
          ) : (
            <p className="text-sm text-[#4A4A4A] border border-[#C5A059]/30 p-4 mb-8" data-testid="bank-details-pending">
              Our bank details are on their way to your inbox — use your order number as the reference.
            </p>
          )}
          <p className="text-[11px] text-[#4A4A4A]/70 mb-8">Your order is held for 48 hours pending payment. A copy of these details has been emailed to you.</p>
          <Link to="/shop" data-testid="placed-continue-link"
            className="inline-block text-xs tracking-[0.25em] uppercase border border-[#1A1A1A] px-8 py-3.5 hover:bg-[#1A1A1A] hover:text-white transition-colors duration-300">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]" data-testid="cart-page">
      <header className="flex items-center gap-4 px-6 md:px-12 py-6 border-b border-[#C5A059]/20 bg-white">
        <Link to="/shop/cart" data-testid="back-to-cart-link" aria-label="Back to cart"
          className="text-[#4A4A4A] hover:text-[#9E7B3A] transition-colors duration-300">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <h1 className="font-serif text-2xl font-light">Checkout</h1>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 lg:grid-cols-5 gap-14">
        <div className="lg:col-span-3 space-y-10">
          <div data-testid="cart-items">
            {items.length === 0 && (
              <p className="text-sm text-[#4A4A4A] bg-white border border-[#C5A059]/25 p-8" data-testid="cart-empty">
                Your cart is empty. <Link to="/shop" className="text-[#9E7B3A] underline">Browse the collection</Link>.
              </p>
            )}
            {items.map((i) => (
              <div key={i.key || i.id} className="flex gap-5 items-center border-b border-[#C5A059]/15 pb-6 mb-6" data-testid={`cart-item-${i.key || i.id}`}>
                <img src={i.image_url || "/assets/logo.png"} alt={i.name} className="w-20 h-24 object-cover bg-white border border-[#C5A059]/15" />
                <div className="flex-1">
                  <p className="font-serif text-lg font-light">{i.name}</p>
                  <p className="text-xs text-[#4A4A4A]">{i.size} · ${i.price.toFixed(2)}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <button onClick={() => setQty(i.key || i.id, i.qty - 1)} data-testid={`qty-minus-${i.key || i.id}`} aria-label="Decrease"
                      className="w-7 h-7 border border-[#C5A059]/40 flex items-center justify-center hover:border-[#9E7B3A] transition-colors duration-300">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="text-sm w-6 text-center">{i.qty}</span>
                    <button onClick={() => setQty(i.key || i.id, i.qty + 1)} data-testid={`qty-plus-${i.key || i.id}`} aria-label="Increase"
                      className="w-7 h-7 border border-[#C5A059]/40 flex items-center justify-center hover:border-[#9E7B3A] transition-colors duration-300">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm mb-3">${(i.price * i.qty).toFixed(2)}</p>
                  <button onClick={() => setQty(i.key || i.id, 0)} data-testid={`remove-item-${i.key || i.id}`} aria-label="Remove"
                    className="text-[#4A4A4A] hover:text-red-700 transition-colors duration-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>

          {items.length > 0 && (
            <div className="space-y-4" data-testid="customer-details-form">
              <p className="text-xs tracking-[0.35em] uppercase text-[#9E7B3A]">Your Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs tracking-widest uppercase">Full Name *</Label>
                  <Input value={customer.name} onChange={(e) => set("name", e.target.value)} data-testid="checkout-name" className="rounded-none bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs tracking-widest uppercase">Email *</Label>
                  <Input type="email" value={customer.email} onChange={(e) => set("email", e.target.value)} data-testid="checkout-email" className="rounded-none bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs tracking-widest uppercase">Phone</Label>
                  <Input value={customer.phone} onChange={(e) => set("phone", e.target.value)} data-testid="checkout-phone" className="rounded-none bg-white" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs tracking-widest uppercase">Street Address *</Label>
                  <Input value={customer.address.line1} onChange={(e) => setAddr("line1", e.target.value)} data-testid="checkout-address" className="rounded-none bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs tracking-widest uppercase">City *</Label>
                  <Input value={customer.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="checkout-city" className="rounded-none bg-white" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs tracking-widest uppercase">State</Label>
                    <Input value={customer.address.state} onChange={(e) => setAddr("state", e.target.value)} data-testid="checkout-state" className="rounded-none bg-white" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs tracking-widest uppercase">Postcode *</Label>
                    <Input value={customer.address.postcode} onChange={(e) => setAddr("postcode", e.target.value)} data-testid="checkout-postcode" className="rounded-none bg-white" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#C5A059]/25 p-8 sticky top-8" data-testid="order-summary">
              <p className="text-xs tracking-[0.35em] uppercase text-[#9E7B3A] mb-6">Summary</p>
              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between"><span className="text-[#4A4A4A]">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between">
                  <span className="text-[#4A4A4A]">Express Shipping</span>
                  <span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between font-medium border-t border-[#C5A059]/20 pt-3">
                  <span>Total</span><span>${total.toFixed(2)} AUD</span>
                </div>
              </div>
              {shipping > 0 && (
                <p className="text-xs text-[#9E7B3A] mb-6">Free express shipping on orders over $300.</p>
              )}
              {!customerValid ? (
                <p className="text-xs text-[#4A4A4A] border border-[#C5A059]/30 p-4" data-testid="fill-details-notice">
                  Fill in your name, email and address above to check out.
                </p>
              ) : (
                <div data-testid="payment-methods">
                  <div className="space-y-3 mb-6">
                    {METHODS.map((m) => (
                      <button key={m.id} type="button" onClick={() => setMethod(m.id)} data-testid={`method-${m.id}`}
                        className={`w-full flex items-center justify-between border px-5 py-4 text-left transition-colors duration-300 ${
                          method === m.id ? "border-[#9E7B3A] bg-[#C5A059]/10" : "border-[#C5A059]/30 hover:border-[#9E7B3A]"
                        }`}>
                        <span>
                          <span className="block text-xs tracking-[0.2em] uppercase">{m.title}</span>
                          <span className="block text-[11px] text-[#4A4A4A] mt-1">{m.note}</span>
                        </span>
                        <span className={`w-4 h-4 rounded-full border-2 transition-colors duration-300 ${method === m.id ? "border-[#9E7B3A] bg-[#9E7B3A]" : "border-[#C5A059]/50"}`} />
                      </button>
                    ))}
                  </div>
                  <button onClick={placeManualOrder} disabled={placing} data-testid="place-order-button"
                    className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
                    {placing ? "Placing Order…" : method === "square" ? "Place Order — Pay via Square" : "Place Order — Pay by Transfer"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
